import { useSelector } from "react-redux";
import { useRef, useState, useEffect, useCallback } from "react";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { AppPlatform, DialogType, DeviceNotification, LocalStorageKey } from "app/types";
import { useScale } from "common/utils";
import { captureScreen } from "features/universal/captureScreen";

export const useNativeMessage = () => {
  const { width, height } = useScale();
  const handleDialog = useDialog();
  const [keyboardHeight, _setKeyboardHeight] = useState(0);
  const keyboardHeightRef = useRef(keyboardHeight);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setKeyboardHeight = (value: number) => {
    _setKeyboardHeight(value);
    keyboardHeightRef.current = value;
  };
  const active = useSelector((state: RootState) => state.user.active);
  const parent = useSelector((state: RootState) => state.user.parent);
  // handleMessage はマウント時に一度だけリスナー登録されるため、
  // 最新の parent を ref 経由で参照する（stale closure 対策）
  const parentRef = useRef(parent);
  useEffect(() => {
    parentRef.current = parent;
  }, [parent]);
  const shakeEnabled = useSelector((state: RootState) => state.config.userConfig.shake_bug_report);
  const shakeRef = useRef({ enabled: shakeEnabled, uid: active?.uid });
  useEffect(() => {
    shakeRef.current = { enabled: shakeEnabled, uid: active?.uid };
  }, [shakeEnabled, active?.uid]);

  // 端末を振ったときに、いまの画面を撮ってからバグ報告の確認を出す。
  // 確認ダイアログが出たあとに撮ると報告したい画面が隠れるため、先に撮る
  const handleShake = useCallback(async () => {
    const { enabled, uid } = shakeRef.current;
    if (!enabled || !uid) return;
    const capture = await captureScreen();
    // 保護者ページは大人向けの画面なので、子ども向けの確認ダイアログではなく
    // parents アプリと同じ報告画面を出す
    const isParentPage = window.location.pathname.startsWith("/parent");
    handleDialog({
      type: isParentPage ? DialogType.PARENT_SHAKE_BUG_REPORT : DialogType.SHAKE_BUG_REPORT,
      value: true,
      args: capture ? { capture } : {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateKeyboardPosition = useCallback((enabled: boolean, offset: number) => {
    if (
      window.ReactNativeWebView &&
      (window.kidsAppPlatform === AppPlatform.Android ||
        window.kidsAppPlatform === AppPlatform.Amazon)
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          command: "keyboardAvoidingViewConfig",
          data: {
            enabled: enabled,
            behavior: "position",
            keyboardVerticalOffset: offset,
          },
        }),
      );
    }
  }, []);

  const handleMessage = (message) => {
    try {
      const { command, data } = JSON.parse(message.data);
      switch (command) {
        case "notifications": {
          const { status: pushStatus } = data;
          if (pushStatus === DeviceNotification.NOT_DETERMINED && parentRef.current) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                command: "registerNotifications",
                data: { uid: parentRef.current.uid },
              }),
            );
          }
          break;
        }
        case "keyboardDidShow": {
          const { event } = data;
          if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
          }
          if (event.height !== keyboardHeightRef.current) {
            localStorage.setItem(LocalStorageKey.KEYBOARD_HEIGHT, event.height);
            setKeyboardHeight(event.height);
          }
          break;
        }
        case "shake":
          handleShake();
          break;
        case "keyboardDidHide":
          if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
          }
          hideTimerRef.current = setTimeout(() => {
            hideTimerRef.current = null;
            handleUpdateKeyboardPosition(false, 0);
          }, 100);
          break;
        default:
          break;
      }
    } catch (error) {
      console.warn(JSON.stringify(message));
      console.warn(JSON.stringify(error));
    }
  };

  useEffect(() => {
    const handleInput = (event: Event) => {
      const element = event.target as HTMLElement;
      if (element && (element.tagName === "INPUT" || element.tagName === "TEXTAREA")) {
        const rect = element.getBoundingClientRect();
        if (keyboardHeightRef.current > 0) {
          const margin = 50;
          const enabled = height - rect.bottom < keyboardHeightRef.current + margin;
          const offset = enabled ? rect.bottom - height + margin : 0;
          handleUpdateKeyboardPosition(enabled, offset);
        }
      }
    };
    if (
      window.ReactNativeWebView &&
      (window.kidsAppPlatform === AppPlatform.Android ||
        window.kidsAppPlatform === AppPlatform.Amazon)
    ) {
      window.addEventListener("focus", handleInput, true);
    }
    return () => {
      if (
        window.ReactNativeWebView &&
        (window.kidsAppPlatform === AppPlatform.Android ||
          window.kidsAppPlatform === AppPlatform.Amazon)
      ) {
        window.removeEventListener("focus", handleInput, true);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (window.kidsAppPlatform === AppPlatform.iOS ||
        window.kidsAppPlatform === AppPlatform.Android) &&
      parent &&
      active
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          command: "getNotifications",
        }),
      );
    }
  }, [parent, active]);

  useEffect(() => {
    if ("ReactNativeWebView" in window) {
      if (window.kidsAppPlatform === AppPlatform.iOS) {
        window.addEventListener("message", handleMessage);
      } else {
        document.addEventListener("message", handleMessage);
      }

      if (
        window.kidsAppPlatform === AppPlatform.Android ||
        window.kidsAppPlatform === AppPlatform.Amazon
      ) {
        const keyboardHeight = localStorage.getItem(LocalStorageKey.KEYBOARD_HEIGHT);
        if (keyboardHeight) {
          setKeyboardHeight(Number(keyboardHeight));
        }
      }
    }

    return () => {
      if ("ReactNativeWebView" in window) {
        if (window.kidsAppPlatform === AppPlatform.iOS) {
          window.removeEventListener("message", handleMessage);
        } else {
          document.removeEventListener("message", handleMessage);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleUpdateKeyboardPosition(false, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);
};
